import { useMemo, useEffect } from 'react';
import Switch from 'react-switch';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { FaRegSave } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { InsertToEsmaToast } from '../../pages/Report/components/InsertToEsmaToast';
import { TbDownload, TbFileExport, TbInfoCircle } from 'react-icons/tb';
import {
  useCheckAIFOrAIFM,
  useGetReportErrors,
  useReplicateStaticData
} from '../../services/report.service';
import { LuInfo, LuUndo2 } from 'react-icons/lu';
import { LuRedo2 } from 'react-icons/lu';
import reportStore from '../../pages/Report/store';
import { Box, Button, Flex, Text, Tooltip } from '@mantine/core';
import ErrorBoundary from '../../components/ErrorBoundary';
import { SelectFunds, Fund } from '../../pages/Report/components/SelectFunds';
import { observer } from 'mobx-react-lite';
import { PermissionGuard } from '../PermissionGuard';
import { idCreator } from '../../pages/Project/utils/idCreator';

interface Jurisdiction {
  id: string;
  reporting_member_state: string;
  country_name: string;
}

interface AIFCheckData {
  is_aif_included: boolean;
  is_aifm_included: boolean;
}

interface ReportTypeTabsProps {
  selectedReportType: string;
  onChangeSelectedReportType: (type: string) => void;
  handleChangeIsMultiFunds: () => void;
  isMultiFunds: boolean;
  onSaveReport: () => void;
  jurisdictions?: Jurisdiction[];
  reportTypes: Record<string, string>;
  isAIF001: boolean;
  onNavigateOtherReport: (jurisdiction: Jurisdiction) => void;
  isViewer?: boolean;
  canManageWorkflow?: boolean;
  canRedo: boolean;
  canUndo: boolean;
  undo: () => void;
  redo: () => void;
  onChangeSelectedFunds: (fundId: string) => void;
  funds: Record<string, Fund>;
  showComparisonReport?: boolean;
  hasChanges?: boolean;
  onBeforeAction?: (action: () => void) => void;
  onOpenExport?: () => void;
}

export const ReportTypeTabs = observer(function ReportTypeTabs({
  selectedReportType,
  onChangeSelectedReportType,
  handleChangeIsMultiFunds,
  isMultiFunds,
  onSaveReport,
  jurisdictions,
  reportTypes,
  isAIF001,
  onNavigateOtherReport,
  isViewer = false,
  canManageWorkflow = true,
  canRedo,
  canUndo,
  undo,
  redo,
  onChangeSelectedFunds,
  funds,
  showComparisonReport,
  hasChanges = false,
  onBeforeAction,
  onOpenExport
}: ReportTypeTabsProps) {
  const { pathname } = useLocation();
  const { reportingMemberState, periodId } = useParams() as {
    reportingMemberState: string;
    periodId: string;
  };
  const [searchParams, setSearchParams] = useSearchParams();

  const jurisdictionId = searchParams.get('jurisdictionId');
  const replicateStaticData = useReplicateStaticData();
  const { isLockedPeriod, isComparisonReport } = reportStore;

  const checkAIFOrAIFM = useCheckAIFOrAIFM({
    params: {
      period_id: periodId
    },
    queryParams: {
      enabled: isViewer,
      onSuccess: res => {
        if (res.is_aifm_included && res.is_aif_included) return;
        if (!res.is_aif_included && res.is_aifm_included) {
          setSearchParams(prev => {
            prev.set('type', 'AIF001');
            return prev;
          });
        }
      }
    }
  });

  const onConfrimReplicate = () => {
    toast.remove();

    toast.promise(
      replicateStaticData.mutateAsync(
        {
          periodId,
          memberState: reportingMemberState.toUpperCase()
        },
        {
          onSuccess: () => {
            setTimeout(() => {
              toast.remove();
            }, 5000);
          },
          onError: () => {
            setTimeout(() => {
              toast.remove();
            }, 5000);
          }
        }
      ),
      {
        loading: 'Inserting...',
        success: `Static template data replicated from ${
          reportingMemberState === 'uk' ? "'FCA'" : "'ESMA'"
        }  to ${reportingMemberState === 'uk' ? "'ESMA'" : "'FCA'"} successfully.`,
        error: err => err?.data?.detail || 'Error with replicate data'
      }
    );
  };

  const onOpenInsert = () => {
    toast.custom(
      t => (
        <InsertToEsmaToast
          onDiscard={() => toast.remove()}
          onSave={onConfrimReplicate}
          isLoading={replicateStaticData.isLoading}
          reportingMemberState={reportingMemberState}
          t={t}
        />
      ),
      {
        duration: 1000000
      }
    );
  };

  const handleOpenInsert = () => {
    if (onBeforeAction) {
      onBeforeAction(onOpenInsert);
      return;
    }

    onOpenInsert();
  };

  const reportType = searchParams.get('reportType');

  const _reportTypes = useMemo(() => {
    if (isViewer) {
      if (
        (checkAIFOrAIFM.data as AIFCheckData | undefined)?.is_aif_included &&
        (checkAIFOrAIFM.data as AIFCheckData | undefined)?.is_aifm_included
      ) {
        return Object.keys(reportTypes);
      }
      if (
        !(checkAIFOrAIFM.data as AIFCheckData | undefined)?.is_aif_included &&
        (checkAIFOrAIFM.data as AIFCheckData | undefined)?.is_aifm_included
      ) {
        return Object.keys(reportTypes).filter(key => key !== 'AIF002');
      }
      if (
        (checkAIFOrAIFM.data as AIFCheckData | undefined)?.is_aif_included &&
        !(checkAIFOrAIFM.data as AIFCheckData | undefined)?.is_aifm_included
      ) {
        return Object.keys(reportTypes).filter(key => key !== 'AIF001');
      }
      return [];
    } else {
      if (reportType === 'grouped') {
        if (jurisdictionId) {
          return Object.keys(reportTypes).filter(key => key !== 'AIF002');
        }
        return Object.keys(reportTypes).filter(key => key !== 'AIF001');
      } else {
        return Object.keys(reportTypes);
      }
    }
  }, [reportTypes, reportType, checkAIFOrAIFM.data, isViewer]);

  useEffect(() => {
    if (jurisdictionId) {
      onChangeSelectedReportType('AIF001');
    }
  }, [jurisdictionId]);

  const { data: reportErrors } = useGetReportErrors({
    periodId,
    queryParams: {
      enabled: !isViewer && !!periodId
    }
  } as any);

  const getReportTypeErrors = reportType => {
    if (reportType === 'AIF002') {
      return reportErrors?.[reportingMemberState.toUpperCase()]?.has_error_aif;
    } else {
      return reportErrors?.[reportingMemberState.toUpperCase()]?.has_error_aifm;
    }
  };

  const applyEditsTooltip =
    reportingMemberState === 'uk'
      ? 'Apply recent changes from the FCA report to the ESMA report. This will overwrite any existing ESMA values.'
      : 'Apply recent changes from the ESMA report to the FCA report. This will overwrite any existing FCA values.';

  const showApplyEditsButton =
    Array.isArray(jurisdictions) &&
    jurisdictions.some(j => j.reporting_member_state === 'UK') &&
    jurisdictions.some(j => j.reporting_member_state !== 'UK');

  return (
    <ErrorBoundary componentName="ReportTypeTabs">
      <Box
        id="report-type-tabs"
        // @ts-ignore
        border="1px solid #f1f3f5"
        p="16px"
        bg="#fff"
        mt="12px"
        h="135px"
        sx={() => ({
          borderRadius: '4px'
        })}
      >
        <Flex id="report-type-tabs-top-row" align="center" justify="space-between">
          <Flex id="report-type-tabs-jurisdictions-row">
            {Array.isArray(jurisdictions) &&
              jurisdictions.map(value => (
                <Flex
                  key={value.id}
                  id={`report-type-tabs-jurisdiction-${idCreator(value.reporting_member_state)}`}
                  component="button"
                  align="center"
                  gap="8px"
                  p="12px"
                  c="black"
                  fz="14px"
                  lh="20px"
                  // @ts-ignore
                  sx={() => ({
                    borderBottom: pathname.includes(
                      `/report/${value.reporting_member_state === 'UK' ? 'fca' : 'esma'}/${value.reporting_member_state.toLowerCase()}`
                    )
                      ? '2px solid #228be6'
                      : '2px solid #dee2e6',
                    whiteSpace: 'no-wrap'
                  })}
                  onClick={() => onNavigateOtherReport(value)}
                >
                  {(reportErrors?.[value.reporting_member_state]?.has_error_aif ||
                    reportErrors?.[value.reporting_member_state]?.has_error_aifm) && (
                    <LuInfo color="#E03131" />
                  )}
                  {value.country_name}
                </Flex>
              ))}
          </Flex>

          {!isViewer && (
            <Flex id="report-type-tabs-top-actions" align="center" gap="16px">
              {!isLockedPeriod && showApplyEditsButton && (
                <PermissionGuard allowed={canManageWorkflow}>
                  <Tooltip
                    label={applyEditsTooltip}
                    maw="321px"
                    multiline
                    bg="gray.6"
                    fz="12px"
                    lh="18px"
                    p="8px"
                  >
                    <Button
                      id="report-type-tabs-apply-static-edits-button"
                      leftSection={<TbFileExport />}
                      rightSection={<TbInfoCircle />}
                      onClick={handleOpenInsert}
                      disabled={!canManageWorkflow}
                      variant="subtle"
                      size="sm"
                    >
                      Insert Static Edits to {reportingMemberState === 'uk' ? 'ESMA' : 'FCA'}
                    </Button>
                  </Tooltip>
                </PermissionGuard>
              )}

              {!isLockedPeriod && (
                <Tooltip
                  hidden={!isComparisonReport && hasChanges}
                  label={
                    isComparisonReport
                      ? 'You cannot save the report in comparison mode'
                      : !hasChanges
                        ? 'No changes to save'
                        : ''
                  }
                >
                  <PermissionGuard allowed={canManageWorkflow}>
                    <Button
                      id="report-type-tabs-save-button"
                      variant="subtle"
                      leftSection={<FaRegSave />}
                      onClick={onSaveReport}
                      size="sm"
                      disabled={isComparisonReport}
                    >
                      Save
                    </Button>
                  </PermissionGuard>
                </Tooltip>
              )}

              <PermissionGuard allowed={canManageWorkflow}>
                <Button
                  id="report-type-tabs-export-button"
                  size="sm"
                  variant="subtle"
                  leftSection={<TbDownload fontSize="16px" />}
                  //rightSection={<TbChevronDown fontSize="16px" />}
                  onClick={onOpenExport}
                >
                  Export
                </Button>
              </PermissionGuard>
            </Flex>
          )}
        </Flex>
        <Flex id="report-type-tabs-bottom-row" align="center" justify="space-between" mt="11px">
          <Flex id="report-type-tabs-type-tabs">
            {_reportTypes.map(key => (
              <Flex
                align="center"
                gap="8px"
                key={key}
                id={`report-type-tabs-type-${key}`}
                onClick={() => onChangeSelectedReportType(key)}
                component="button"
                p="12px"
                c="black"
                fz="14px"
                lh="20px"
                // @ts-ignore
                sx={() => ({
                  borderBottom:
                    selectedReportType === key ? '2px solid #228be6' : '2px solid #dee2e6',
                  whiteSpace: 'no-wrap'
                })}
              >
                {getReportTypeErrors(key) && <LuInfo color="#E03131" />}
                {reportTypes[key]}
              </Flex>
            ))}
          </Flex>
          <Flex id="report-type-tabs-bottom-actions" align="center" gap="16px">
            {!isAIF001 && (
              <SelectFunds funds={Object.values(funds)} onChange={onChangeSelectedFunds} />
            )}
            {!isViewer && (
              <Flex id="report-type-tabs-history-actions" align="center" gap="10px">
                <LuUndo2 fontSize="16px" onClick={undo} color={canUndo ? '#228be6' : '#909296'} />
                <LuRedo2 fontSize="16px" onClick={redo} color={canRedo ? '#228be6' : '#909296'} />
              </Flex>
            )}

            {!isAIF001 && (
              <Flex id="report-type-tabs-multi-view" align="center" gap="10px">
                <Text id="report-type-tabs-multi-view-label" fz="14px" lh="20px">
                  Multi-AIF View
                </Text>
                <Switch
                  id="report-type-tabs-multi-view-switch"
                  uncheckedIcon={false}
                  width={42}
                  height={20}
                  onChange={handleChangeIsMultiFunds}
                  checkedIcon={false}
                  handleDiameter={15}
                  checked={isMultiFunds}
                  onColor="#228be6"
                  offColor="#E9ECEF"
                />
              </Flex>
            )}

            {showComparisonReport && (
              <Flex id="report-type-tabs-comparison-view" align="center" gap="10px">
                <Text id="report-type-tabs-comparison-view-label" fz="14px" lh="20px">
                  Comparison Mode
                </Text>
                <Switch
                  id="report-type-tabs-comparison-switch"
                  uncheckedIcon={false}
                  width={42}
                  height={20}
                  onChange={() => reportStore.setIsComparisonReport(!isComparisonReport)}
                  checkedIcon={false}
                  handleDiameter={15}
                  checked={isComparisonReport}
                  onColor="#228be6"
                  offColor="#E9ECEF"
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Box>
    </ErrorBoundary>
  );
});
